import React, { useEffect, useMemo, useRef, useState } from 'react';
import SectionHeader from './SectionHeader';

const SO_SCRIPT_ID = 'scheduleonce-embed-script-checkin';
const SO_SCRIPT_SRC = 'https://cdn.oncehub.com/mergedjs/so.js';
const LOG_ALL_SCHEDULER_MESSAGES = false;

const ADVISOR_DIV_IDS = {
  bradford: 'SOIDIV_Bradford60',
  matt: 'SOIDIV_Matt60',
};

function resolveAdvisorKey(secondaryAdvisor) {
  const key = String(secondaryAdvisor || '').trim().toLowerCase();
  if (key === 'bradford' || key === 'matt') return key;
  return 'matt';
}

function initScheduleOnceEmbed() {
  if (window.SO && typeof window.SO.reInit === 'function') {
    window.SO.reInit();
  }
}

function isAllowedOnceHubOrigin(origin) {
  try {
    const host = new URL(origin).hostname.toLowerCase();
    return host === 'oncehub.com'
      || host.endsWith('.oncehub.com')
      || host === 'onceplatform.com'
      || host.endsWith('.onceplatform.com');
  } catch (_err) {
    return false;
  }
}

function parseMessageData(raw) {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function isOnceHubBookingMessage(event, parsedData) {
  if (!isAllowedOnceHubOrigin(event.origin)) return false;

  const eventType = parsedData?.type;
  if (!eventType || typeof eventType !== 'string') return false;

  const lowered = eventType.toLowerCase();
  if (lowered === 'oncehub.booking.scheduled') return true;

  return lowered.includes('booking') || lowered.includes('confirm') || lowered.includes('scheduled');
}

function getMessageFilterDecision(event, parsedData) {
  if (!isAllowedOnceHubOrigin(event.origin)) {
    return { accepted: false, reason: 'origin_not_allowed' };
  }

  const eventType = parsedData?.type;
  if (!eventType || typeof eventType !== 'string') {
    return { accepted: false, reason: 'missing_or_invalid_type' };
  }

  const lowered = eventType.toLowerCase();
  if (lowered === 'oncehub.booking.scheduled') {
    return { accepted: true, reason: 'exact_oncehub_booking_scheduled' };
  }

  const accepted = lowered.includes('booking')
    || lowered.includes('confirm')
    || lowered.includes('scheduled');

  return {
    accepted,
    reason: accepted ? 'matched_fallback_keywords' : 'type_filtered_out',
  };
}

function formatSchedulerDateTime(rawValue) {
  if (!rawValue || typeof rawValue !== 'string') return '';

  const normalized = rawValue.trim();
  if (!normalized) return '';

  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) {
    return normalized;
  }

  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  } catch (_err) {
    return normalized;
  }
}

function setParam(url, key, value) {
  if (value !== undefined && value !== null && String(value).trim() !== '') {
    url.searchParams.set(key, String(value).trim());
  }
}

function buildSchedulerHref(baseUrl, formData, clientName, prefill) {
  const fallback = 'https://go.oncehub.com/IMReview';
  const contact = formData?.contact || {};
  const pre = prefill || {};

  const fullName = contact.name || clientName || pre.name || '';
  const email = contact.email || pre.email || '';
  const phone = contact.phone || pre.phone || '';

  try {
    const url = new URL(baseUrl || fallback);

    setParam(url, 'Name', fullName);
    setParam(url, 'Email', email);
    setParam(url, 'phone', phone);
    setParam(url, 'Phone', phone);
    setParam(url, 'mobile', phone);
    setParam(url, 'Phone number', phone);
    setParam(url, 'Investment Management Update Done', 'Yes');

    return url.toString().replace(/\+/g, '%20');
  } catch (_err) {
    return baseUrl || fallback;
  }
}

function getMeetingStatus(value) {
  if (!value || typeof value !== 'object') return 'not_scheduled';

  const explicitStatus = String(value.meeting_status || '').trim().toLowerCase();
  if (explicitStatus === 'canceled' || explicitStatus === 'cancelled') {
    return 'canceled';
  }
  if (explicitStatus === 'scheduled') {
    return 'scheduled';
  }

  const bookingInfo = value.booking_data?.bookingInfo;
  if (value.booking_detected) return 'scheduled';
  if (bookingInfo?.statusCode && String(bookingInfo.statusCode).trim() === '1') {
    return 'scheduled';
  }
  if (Array.isArray(bookingInfo?.MeetingTimes) && bookingInfo.MeetingTimes.length > 0) {
    return 'scheduled';
  }

  const bookedStatus = String(value.booking_data?.bookedstatus || '').trim().toLowerCase();
  if (bookedStatus === 'canceled' || bookedStatus === 'cancelled') {
    return 'canceled';
  }
  if (bookedStatus === 'scheduled') {
    return 'scheduled';
  }

  return 'not_scheduled';
}

function resolveSchedulerPage(schedulerHref) {
  const fallback = 'IMReview';
  try {
    const url = new URL(schedulerHref || '');
    const page = (url.pathname || '').replace(/^\/+/, '').trim();
    return page || fallback;
  } catch (_err) {
    return fallback;
  }
}

export default function StepScheduleMeeting({
  data,
  onChange,
  schedulingUrl,
  formData,
  clientName,
  secondaryAdvisor,
  prefill,
}) {
  const value = useMemo(() => data || {}, [data]);
  const [bookingDetected, setBookingDetected] = useState(
    getMeetingStatus(value) === 'scheduled',
  );
  const [scriptLoadFailed, setScriptLoadFailed] = useState(false);
  const mountedRef = useRef(true);
  const latestValueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  const advisorKey = useMemo(
    () => resolveAdvisorKey(secondaryAdvisor),
    [secondaryAdvisor],
  );
  const schedulerHref = useMemo(
    () => buildSchedulerHref(schedulingUrl, formData, clientName, prefill),
    [schedulingUrl, formData, clientName, prefill],
  );
  const soPage = useMemo(() => resolveSchedulerPage(schedulerHref), [schedulerHref]);
  const divId = ADVISOR_DIV_IDS[advisorKey] || ADVISOR_DIV_IDS.matt;
  const soQueryParams = useMemo(() => {
    try {
      const idx = schedulerHref.indexOf('?');
      return idx >= 0 ? schedulerHref.slice(idx + 1) : '';
    } catch (_err) {
      return '';
    }
  }, [schedulerHref]);
  const useScriptEmbed = useMemo(() => {
    if (typeof window === 'undefined') return true;
    const host = String(window.location?.hostname || '').toLowerCase();
    return host !== 'localhost' && host !== '127.0.0.1';
  }, []);

  useEffect(() => {
    latestValueRef.current = value;
    setBookingDetected(getMeetingStatus(value) === 'scheduled');
  }, [value]);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  function persistBooking(bookingData) {
    const detected = bookingData?.errorStatus === 0
      || !!bookingData?.bookingInfo
      || String(bookingData?.bookedstatus || '').toLowerCase() === 'scheduled';

    setBookingDetected(detected);
    onChangeRef.current({
      ...latestValueRef.current,
      _visited: true,
      booking_detected: detected,
      meeting_status: detected ? 'scheduled' : 'not_scheduled',
      booking_data: bookingData,
    });
  }

  function markScheduledManually() {
    persistBooking({
      bookedstatus: 'scheduled',
      source: 'manual_confirm',
    });
  }

  const bookingData = value.booking_data || null;
  const meetingStatus = getMeetingStatus(value);
  const rawScheduledAt = bookingData?.bookingInfo?.MeetingTimes?.[0]?.CustomerTime
    || bookingData?.bookingInfo?.MeetingTimes?.[0]?.SOTime
    || bookingData?.bookingInfo?.MeetingTimes?.[0]?.UTCTime
    || null;
  const scheduledAt = formatSchedulerDateTime(rawScheduledAt);
  const manageRescheduleLink = bookingData?.bookingInfo?.CancelRescheduleLink || schedulerHref;

  useEffect(() => {
    if (!value._visited) {
      onChange({ ...value, _visited: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    window.SOAfterConfirmationFunction = function (bookingDataFromWidget) {
      if (!mountedRef.current) return;
      persistBooking(bookingDataFromWidget);
    };

    function handleMessage(event) {
      if (!mountedRef.current) return;
      const parsedData = parseMessageData(event.data);
      const eventType = String(parsedData?.type || '');
      const decision = getMessageFilterDecision(event, parsedData);

      if (LOG_ALL_SCHEDULER_MESSAGES) {
        console.info('[Check-In Schedule] postMessage event', {
          origin: event.origin,
          eventType,
          accepted: decision.accepted,
          reason: decision.reason,
          parsedData,
          rawData: event.data,
        });
      }

      if (!decision.accepted) return;
      if (!isOnceHubBookingMessage(event, parsedData)) return;

      const payload = parsedData?.payload || parsedData?.bookingInfo || null;
      if (!payload) return;

      const bookingId = payload?.id || null;
      persistBooking({
        errorStatus: 0,
        bookedstatus: 'scheduled',
        bookingInfo: payload,
        oncehub_booking_id: bookingId,
      });
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.SOAfterConfirmationFunction = undefined;
      window.removeEventListener('message', handleMessage);
      mountedRef.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!useScriptEmbed) return () => {};

    const container = document.getElementById(divId);
    if (container && soQueryParams) {
      container.setAttribute('data-so-qry-prm', soQueryParams);
    }

    const existing = document.getElementById(SO_SCRIPT_ID);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.id = SO_SCRIPT_ID;
    script.type = 'text/javascript';
    script.src = `${SO_SCRIPT_SRC}?v=${Date.now()}`;
    script.async = true;
    script.onload = () => {
      setScriptLoadFailed(false);
      initScheduleOnceEmbed();
    };
    script.onerror = () => {
      setScriptLoadFailed(true);
    };
    document.body.appendChild(script);

    return () => {
      script.remove();
    };
  }, [soQueryParams, divId, useScriptEmbed]);

  const greetingName = (clientName || '').split(' ')[0];
  const headerTitle = meetingStatus === 'scheduled'
    ? `You're booked${greetingName ? `, ${greetingName}` : ''}!`
    : meetingStatus === 'canceled'
      ? `Your prior booking was canceled${greetingName ? `, ${greetingName}` : ''}`
      : `Welcome${greetingName ? `, ${greetingName}` : ''} — let's get your review on the calendar`;
  const headerTip = meetingStatus === 'scheduled'
    ? 'Your meeting is set. Continue through the brief IM Review questionnaire.'
    : meetingStatus === 'canceled'
      ? 'Your previous meeting is no longer on the calendar. Please pick a new time below before continuing.'
      : 'Pick a time that works for you. Once your meeting is scheduled, continue to the rest of the review.';

  return (
    <div>
      <SectionHeader
        title={headerTitle}
        tip={headerTip}
        showAnnualNote={false}
      />

      <div className="cashflow-section-card">
        {meetingStatus === 'scheduled' ? (
          <div className="alert alert-success mb-3">
            <div className="fw-semibold mb-1">
              <i className="bi bi-check-circle me-1" />
              Meeting already scheduled
            </div>
            {scheduledAt && (
              <div className="small mb-0">Scheduled time: {scheduledAt}</div>
            )}
          </div>
        ) : meetingStatus === 'canceled' ? (
          <div className="alert alert-warning mb-3">
            <div className="fw-semibold mb-1">
              <i className="bi bi-calendar2-x me-1" />
              Previous meeting canceled
            </div>
            {scheduledAt && (
              <div className="small mb-2">Most recent canceled time: {scheduledAt}</div>
            )}
            <div className="small mb-0">Please book a new time below to continue.</div>
          </div>
        ) : useScriptEmbed && !scriptLoadFailed ? (
          <div
            id={divId}
            data-so-page={soPage}
            data-height="550"
            data-style="border: 1px solid #d8d8d8; min-width: 290px; max-width: 900px;"
            data-psz="00"
            data-so-qry-prm={soQueryParams}
          />
        ) : (
          <>
            <div className="alert alert-secondary mb-3">
              Embedded scheduler is not available in this environment. Use the button below to schedule.
            </div>
            <iframe
              title="Schedule meeting"
              src={schedulerHref}
              style={{ width: '100%', minHeight: 650, border: '1px solid #d8d8d8', borderRadius: 6 }}
            />
          </>
        )}

        <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">
          {bookingDetected ? (
            <a
              href={manageRescheduleLink}
              className="btn btn-outline-secondary btn-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="bi bi-box-arrow-up-right me-1" />
              Manage / reschedule
            </a>
          ) : (
            <div className="d-flex align-items-center gap-2">
              <span className="text-muted small">
                Having trouble with the embedded scheduler?
              </span>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={markScheduledManually}
                title="Use this if you see the booking confirmation inside OnceHub."
              >
                <i className="bi bi-check2-circle me-1" />
                I&apos;ve scheduled my meeting
              </button>
              <a
                href={schedulerHref}
                className="btn btn-outline-danger btn-sm"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => onChange({ ...value, _visited: true, opened_external: true })}
              >
                <i className="bi bi-box-arrow-up-right me-1" />
                Open in new tab
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
